import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Modal, Button, Icon, Progress, Table, Message, Segment} from 'semantic-ui-react';
import {FilePond} from 'react-filepond';
import {Actions} from '../../../common/actions';
import 'filepond/dist/filepond.min.css';

const {savePartAttachments, updatePartHaveFiles, searchDealerEmergingOrder} = Actions;

const INSTRUCTIONS = [
  'El nombre del archivo debe seguir el formato: CODIGO1-CODIGO2-nombre.pdf (mínimo un código antes del último guion).',
  'El nombre del archivo y los códigos no deben contener' +
  ' espacios ni caracteres especiales (únicamente los guiones separadores).',
  'Si un repuesto ya tiene un archivo MSDS cargado, este será reemplazado por el nuevo documento.',
  'Un mismo archivo PDF puede asociarse a múltiples códigos de repuesto separando los códigos con guiones.',
  'Solo se procesarán archivos con formato válido. Los archivos inválidos se mostrarán en una lista de errores.',
  'Verifique los códigos de repuesto en la tabla antes de iniciar la carga para evitar asociaciones incorrectas.',
  'No cierre esta ventana mientras la carga esté en progreso.',
];

const handleFileAction = (file, filename, action) => {
  const url = URL.createObjectURL(file);
  if (action === 'download') {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else if (action === 'view') {
    window.open(url, '_blank');
  }
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const ActionButtons = ({item, onRemove}) => {
  return (
    <React.Fragment>
      <Button size='mini' basic color='teal' type='button'
        onClick={() => handleFileAction(item.file, item.filename, 'view')}>
        <Icon name='eye' /> Ver
      </Button>
      <Button size='mini' basic color='blue' type='button'
        onClick={() => handleFileAction(item.file, item.filename, 'download')}>
        <Icon name='download' /> Descargar
      </Button>
      <Button size='mini' basic color='red' type='button' onClick={() => onRemove(item.id)}>
        <Icon name='trash alternate outline' /> Quitar
      </Button>
    </React.Fragment>
  );
};

const getProcessedFiles = (fileItems, resolvedMap) => {
  const rawReady = [];
  const invalid = [];

  fileItems.map((fi) => fi.file).forEach((file) => {
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    const parts = nameWithoutExt.split('-');

    if (parts.length < 2) {
      invalid.push(file.name);
      return;
    }

    const codes = parts.slice(0, -1);
    codes.forEach((code) => {
      rawReady.push({
        'partCode': code.trim().toUpperCase(),
        'file': file,
        'filename': file.name,
        'id': `${code}-${file.name}`,
      });
    });
  });

  const grouped = {};
  rawReady.forEach((item) => {
    if (!grouped[item.partCode]) {
      grouped[item.partCode] = [];
    }
    if (!grouped[item.partCode].find((x) => x.id === item.id)) {
      grouped[item.partCode].push(item);
    }
  });

  const ready = [];
  const conflicts = [];

  Object.keys(grouped).forEach((code) => {
    if (grouped[code].length === 1) {
      ready.push(grouped[code][0]);
    } else if (resolvedMap[code]) {
      const selected = grouped[code].find((x) => x.id === resolvedMap[code]);
      if (selected) {
        ready.push(selected);
      } else {
        conflicts.push({'partCode': code, 'options': grouped[code]});
      }
    } else {
      conflicts.push({'partCode': code, 'options': grouped[code]});
    }
  });

  return {ready, invalid, conflicts};
};

const InstructionsMessage = () => {
  return (
    <Message info icon>
      <Icon name='info circle' />
      <Message.Content>
        <Message.Item>
          <span style={{'fontWeight': 'bold', 'fontSize': '1.2em'}}>
            Indicaciones importantes
          </span>
        </Message.Item>
        <Message.List>
          {INSTRUCTIONS.map((text, index) => (
            <Message.Item key={index}>{text}</Message.Item>
          ))}
        </Message.List>
      </Message.Content>
    </Message>
  );
};

const InvalidFilesMessage = ({invalidList}) => {
  if (invalidList.length === 0) {
    return null;
  }

  return (
    <Message negative>
      <Message.Content>
        <Message.Item>
          <span style={{'fontWeight': 'bold', 'fontSize': '1.1em'}}>
            Archivos con formato incorrecto
          </span>
        </Message.Item>
        <p style={{'marginTop': '0.5em'}}>
          Los siguientes archivos no cumplen el formato (codigo1-codigo2-archivo.pdf):
        </p>
        <Message.List>
          {invalidList.map((name, index) => (
            <Message.Item key={index}>{name}</Message.Item>
          ))}
        </Message.List>
      </Message.Content>
    </Message>
  );
};

const NotFoundMessage = ({notFoundList}) => {
  if (notFoundList.length === 0) {
    return null;
  }

  return (
    <Message warning>
      <Message.Content>
        <Message.Item>
          <span style={{'fontWeight': 'bold', 'fontSize': '1.1em'}}>
            Repuestos no encontrados
          </span>
        </Message.Item>
        <p style={{'marginTop': '0.5em'}}>
          Los siguientes códigos no existen o no tienen sustitutos/despieces válidos y fueron omitidos:
        </p>
        <Message.List>
          {notFoundList.map((code, index) => (
            <Message.Item key={index}><strong>{code}</strong></Message.Item>
          ))}
        </Message.List>
      </Message.Content>
    </Message>
  );
};

const ConflictsTable = ({list, onResolve}) => {
  if (list.length === 0) {
    return null;
  }

  return (
    <Segment color='red'>
      <h4 style={{'color': '#db2828'}}>Conflictos detectados (Mismo código)</h4>
      <p>Seleccione qué documento desea conservar para cada repuesto:</p>
      <Table celled striped size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Repuesto</Table.HeaderCell>
            <Table.HeaderCell textAlign='center'>Opciones de Documento</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {list.map((c) => (
            <Table.Row key={c.partCode}>
              <Table.Cell><strong>{c.partCode}</strong></Table.Cell>
              <Table.Cell textAlign='center'>
                {c.options.map((opt) => (
                  <Button
                    key={opt.id}
                    size='mini'
                    color='orange'
                    onClick={() => onResolve(c.partCode, opt.id)}
                    style={{'margin': '2px'}}
                  >
                    <Icon name='check' /> {opt.filename}
                  </Button>
                ))}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Segment>
  );
};

const FilesTable = ({list, title, isExisting, onRemove}) => {
  if (list.length === 0) {
    return null;
  }

  return (
    <Segment>
      <h4 style={{'color': isExisting ? '#e0b404' : '#555'}}>{title}</h4>
      <Table celled striped size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Repuesto</Table.HeaderCell>
            <Table.HeaderCell>Documento PDF</Table.HeaderCell>
            {!isExisting && <Table.HeaderCell textAlign='center'>Acciones</Table.HeaderCell>}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {list.map((item) => (
            <Table.Row key={item.id}>
              <Table.Cell><strong>{item.partCode}</strong></Table.Cell>
              <Table.Cell>{item.filename}</Table.Cell>
              {!isExisting && (
                <Table.Cell textAlign='center'>
                  <ActionButtons item={item} onRemove={onRemove} />
                </Table.Cell>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Segment>
  );
};

const useUploadLists = () => {
  const [readyList, setReadyList] = useState([]);
  const [existingList, setExistingList] = useState([]);
  const [invalidList, setInvalidList] = useState([]);
  const [notFoundList, setNotFoundList] = useState([]);
  const [localNotFound, setLocalNotFound] = useState([]);
  const [conflictList, setConflictList] = useState([]);
  const [resolvedConflicts, setResolvedConflicts] = useState({});

  const resetLists = () => {
    setReadyList([]);
    setExistingList([]);
    setInvalidList([]);
    setNotFoundList([]);
    setLocalNotFound([]);
    setConflictList([]);
    setResolvedConflicts({});
  };

  return {
    readyList, setReadyList,
    existingList, setExistingList,
    invalidList, setInvalidList,
    notFoundList, setNotFoundList,
    localNotFound, setLocalNotFound,
    conflictList, setConflictList,
    resolvedConflicts, setResolvedConflicts,
    resetLists,
  };
};

const useUploadCtrl = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const resetCtrl = () => {
    setModalIsOpen(false);
    setProgress(0);
    setIsUploading(false);
  };

  return {modalIsOpen, setModalIsOpen, progress, setProgress, isUploading, setIsUploading, resetCtrl};
};

const useUploadMachine = () => {
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isChecking, setIsChecking] = useState(false);
  const [prevLoading, setPrevLoading] = useState(false);

  const resetMachine = () => {
    setCurrentIndex(-1);
    setIsChecking(false);
    setPrevLoading(false);
  };

  return {currentIndex, setCurrentIndex, isChecking, setIsChecking, prevLoading, setPrevLoading, resetMachine};
};
// eslint-disable-next-line max-statements
const MassUploadModal = () => {
  const dispatch = useDispatch();
  const dealerOrders = useSelector((state) => state.dealerEmergingOrders || {});

  const [rawFiles, setRawFiles] = useState([]);
  const lists = useUploadLists();
  const ctrl = useUploadCtrl();
  const machine = useUploadMachine();

  const closeModal = () => {
    ctrl.resetCtrl();
    lists.resetLists();
    machine.resetMachine();
    setRawFiles([]);
  };

  const onUpdateFiles = (fileItems) => {
    setRawFiles(fileItems);
  };

  useEffect(() => {
    const {ready, invalid, conflicts} = getProcessedFiles(rawFiles, lists.resolvedConflicts);
    lists.setReadyList(ready);
    lists.setInvalidList(invalid);
    lists.setConflictList(conflicts);
    lists.setExistingList([]);
    lists.setNotFoundList([]);
  }, [rawFiles, lists.resolvedConflicts]);

  const handleRemove = (id) => {
    lists.setReadyList(lists.readyList.filter((item) => item.id !== id));
  };

  const handleResolveConflict = (partCode, selectedId) => {
    lists.setResolvedConflicts((prev) => ({...prev, [partCode]: selectedId}));
  };

  const handleUpload = () => {
    ctrl.setIsUploading(true);
    ctrl.setProgress(0);
    lists.setLocalNotFound([]);
    machine.setCurrentIndex(0);
  };

  useEffect(() => {
    if (machine.currentIndex >= 0 && machine.currentIndex < lists.readyList.length) {
      const item = lists.readyList[machine.currentIndex];
      machine.setIsChecking(true);
      dispatch(searchDealerEmergingOrder(item.partCode, 1));
    } else if (machine.currentIndex === lists.readyList.length && lists.readyList.length > 0) {
      if (lists.localNotFound.length > 0) {
        lists.setNotFoundList([...new Set(lists.localNotFound)]);
        ctrl.setIsUploading(false);
      } else {
        setTimeout(() => closeModal(), 1000);
      }
      machine.setCurrentIndex(-1);
    }
  }, [machine.currentIndex, lists.readyList.length]);

  useEffect(() => {
    const searchLoading = dealerOrders.loading === true;
    const searchParts = dealerOrders.parts || [];

    if (machine.isChecking && machine.prevLoading === true && searchLoading === false) {
      machine.setIsChecking(false);
      const item = lists.readyList[machine.currentIndex];

      if (searchParts && searchParts.length > 0) {
        const formData = new FormData();
        formData.append('file', item.file, item.filename);
        dispatch(savePartAttachments(item.partCode, formData));
        dispatch(updatePartHaveFiles(item.partCode, 'S'));
      } else {
        lists.setLocalNotFound((prev) => [...prev, item.partCode]);
      }

      ctrl.setProgress(Math.round(((machine.currentIndex + 1) / lists.readyList.length) * 100));
      machine.setCurrentIndex((prev) => prev + 1);
    }

    machine.setPrevLoading(searchLoading);
  }, [dealerOrders.loading]);

  const renderContent = () => {
    if (ctrl.isUploading) {
      return (
        <Segment basic padded='very' textAlign='center'>
          <h4>Subiendo documentos...</h4>
          <Progress percent={ctrl.progress} indicating progress />
        </Segment>
      );
    }

    return (
      <React.Fragment>
        <InstructionsMessage />
        <FilePond
          allowMultiple={true}
          credits={false}
          labelIdle='Arrastra múltiples PDF aquí o <span class="filepond--label-action">Navegador</span>'
          onupdatefiles={onUpdateFiles}
        />
        <InvalidFilesMessage invalidList={lists.invalidList} />
        <ConflictsTable list={lists.conflictList} onResolve={handleResolveConflict} />
        <NotFoundMessage notFoundList={lists.notFoundList} />
        <FilesTable list={lists.readyList} title='Archivos listos para cargar'
          isExisting={false} onRemove={handleRemove} />
        <FilesTable list={lists.existingList} title='Repuestos con documentos ya existentes' isExisting={true} />
      </React.Fragment>
    );
  };

  return (
    <Modal
      size='large'
      open={ctrl.modalIsOpen}
      trigger={
        <Button type='button' color='green' onClick={() => ctrl.setModalIsOpen(true)}>
          <Icon name='upload' /> CARGA MASIVA MSDS
        </Button>
      }
    >
      <Modal.Header>Carga Masiva de Documentos MSDS</Modal.Header>
      <Modal.Content scrolling>
        {renderContent()}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={closeModal} type='button' disabled={ctrl.isUploading}>
          Cancelar
        </Button>
        <Button
          positive
          onClick={handleUpload}
          type='button'
          disabled={lists.readyList.length === 0 || ctrl.isUploading || lists.conflictList.length > 0}
        >
          <Icon name='save' /> Iniciar Carga
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default MassUploadModal;
